import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mini } from '../utils';
import './CertCarrusel.css';

/** Avance automático: un certificado cada PASO_MS, salvo en los ESPERA_MS después de que la persona lo usó. */
const PASO_MS = 4000;
const ESPERA_MS = 6000;

/**
 * Certificados en el celular: pestañas por marca y un carrusel con un certificado a la vez (se asoma el siguiente).
 * Se desliza con el dedo; tocar una pestaña lleva a su primer certificado y tocar la tarjeta lo abre en grande.
 * Pasa solo mientras se ve en pantalla, salvo que la persona lo esté usando, el certificado esté abierto o el sistema
 * pida reducir el movimiento. Solo se mueve el carrusel, nunca la página.
 * @param {Object} props
 * @param {import('../data/viajes').Certificacion[]} props.lista  certificados en orden de marca
 * @param {import('../services/certificaciones').GrupoCertificaciones[]} props.grupos  una pestaña por marca
 * @param {(i: number) => void} props.onAbrir  abre el certificado i de `lista`
 * @param {boolean} props.pausado  true mientras hay un certificado abierto
 */
export default function CertCarrusel({ lista, grupos, onAbrir, pausado }) {
  const pista = useRef(null);
  const [activo, setActivo] = useState(0);
  const activoRef = useRef(0);
  const usado = useRef(0);
  const enPantalla = useRef(false);

  // centra el certificado i dentro de la pista
  const ir = useCallback((i) => {
    const p = pista.current, t = p && p.children[i];
    if (t) p.scrollTo({ left: t.offsetLeft - (p.clientWidth - t.offsetWidth) / 2, behavior: 'smooth' });
  }, []);

  // el certificado activo es el más cercano al centro (se calcula una vez por cuadro mientras se desliza)
  useEffect(() => {
    const p = pista.current;
    let raf = 0;
    const medir = () => {
      raf = 0;
      const centro = p.scrollLeft + p.clientWidth / 2;
      let mejor = 0, distancia = Infinity;
      [...p.children].forEach((t, i) => {
        const d = Math.abs(t.offsetLeft + t.offsetWidth / 2 - centro);
        if (d < distancia) { distancia = d; mejor = i; }
      });
      if (mejor !== activoRef.current) { activoRef.current = mejor; setActivo(mejor); }
    };
    const alDeslizar = () => { if (!raf) raf = requestAnimationFrame(medir); };
    p.addEventListener('scroll', alDeslizar, { passive: true });
    return () => { p.removeEventListener('scroll', alDeslizar); cancelAnimationFrame(raf); };
  }, []);

  // mientras la persona lo usa, el carrusel no avanza solo
  useEffect(() => {
    const p = pista.current, eventos = ['pointerdown', 'touchstart', 'wheel'];
    const usar = () => { usado.current = Date.now(); };
    eventos.forEach((ev) => p.addEventListener(ev, usar, { passive: true }));
    return () => eventos.forEach((ev) => p.removeEventListener(ev, usar));
  }, []);

  // solo avanza cuando se ve al menos la mitad del carrusel
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver(([e]) => { enPantalla.current = e.isIntersecting; }, { threshold: 0.5 });
    io.observe(pista.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setInterval(() => {
      if (pausado || !enPantalla.current || document.hidden || Date.now() - usado.current < ESPERA_MS) return;
      ir((activoRef.current + 1) % lista.length);
    }, PASO_MS);
    return () => clearInterval(t);
  }, [pausado, lista.length, ir]);

  // la pestaña iluminada es la de la marca del certificado activo
  const marca = grupos.reduce((g, x) => (activo >= x.primero ? x : g), grupos[0]);

  return (
    <div className="certcar">
      <div className="certcar__marcas" role="group" aria-label="Certificaciones por marca">
        {grupos.map((g) => (
          <button key={g.id} type="button" className={g === marca ? 'is-on' : ''} aria-pressed={g === marca}
            onClick={() => { usado.current = Date.now(); ir(g.primero); }}>
            {g.nombre}<span>{g.total}</span>
          </button>
        ))}
      </div>
      <div className="certcar__pista" ref={pista} role="region" aria-roledescription="carrusel" aria-label="Certificaciones">
        {lista.map((ce, i) => (
          <button key={ce.nombre} type="button" className="card sobre__cert certcar__cert" onClick={() => onAbrir(i)}>
            <span className="sobre__cert-img"><img src={mini(ce.imagen)} alt={ce.nombre} loading="lazy" decoding="async" /></span>
            <span className="sobre__cert-n">{ce.nombre}</span>
            <span className="sobre__cert-a">{ce.fechaInline || ce.fecha}</span>
          </button>
        ))}
      </div>
      <div className="certcar__puntos" aria-hidden="true">
        {lista.map((ce, i) => <span key={ce.nombre} className={i === activo ? 'is-on' : ''} />)}
      </div>
    </div>
  );
}
